
import { Component, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import Notification from '@/mixins/Notification';
import { mixins } from 'vue-class-component';
import { Venue } from '@/interfaces/models/Venue';
import { Tip } from '@/interfaces/models/Tip';
import VRangePicker from '@/components/shared/form/VRangePicker.vue';
import ReportingTipsApiService from '@/api/http/ReportingTipsApiService';
import ReportingExportService, { ExportFormat, ReportType } from '@/api/http/ReportingExportService';
import { Download } from '@/interfaces/models/Download';
import { currencyByVenue } from '@/util/helper';
import moment from 'moment';

const app = namespace('app');
const download = namespace('download');
const api = new ReportingTipsApiService();

interface Selection {
  from?: string;
  to?: string;
  venue?: string;
}

@Component({
  components: { VRangePicker },
})
export default class Start extends mixins(Notification) {
  @app.State('venues') public venues!: Venue[];
  @download.Action('addDownload') public addDownload!: (d: Download) => void;

  public venue: string = '';
  public range = {
    from: '',
    to: '',
  };

  get currency(): string {
    const activeVenue = this.venues.find((v) => v._id === this.venue);

    return currencyByVenue(activeVenue);
  }

  get canFetch(): boolean {
    return !!this.selection.venue && !!this.selection.from && !!this.selection.to;
  }

  public tipsData: Tip | null = null;

  get selection() {
    const selection: Selection = {};
    if (this.venue) {
      selection.venue = this.venue;
    }
    if (this.range && this.range.from && this.range.to) {
      selection.from = this.range.from;
      selection.to = this.range.to;
    }

    return selection;
  }

  /**
   * The tips CSV, generated by the reporting service (`tips`) instead of
   * export-service. It is an asynchronous job that downloads here, where the
   * old one was e-mailed; DownloadHandler polls it and opens the signed URL.
   *
   * One venue per run and csv only — the service rejects anything else.
   */
  public async exportCsv() {
    const { venue, from, to } = this.selection;
    if (!venue || !from || !to) {
      return;
    }

    this.$startLoading('tipsExport');
    try {
      const { data: job } = await new ReportingExportService().createExport({
        reportType: ReportType.TIPS,
        format: ExportFormat.CSV,
        filters: {
          // The API takes whole days only: `YYYY-MM-DD/YYYY-MM-DD`.
          period: `${moment(from).format('YYYY-MM-DD')}/${moment(to).format('YYYY-MM-DD')}`,
          venues: [venue],
        },
      });

      this.addDownload({ file: job.jobId, timeout: 300, handler: 'export', inProgress: false, done: false });
      this.notifyInfo('notification.downloadInProgress');
    } catch (error) {
      this.notifyError('notification.500');
    } finally {
      this.$stopLoading('tipsExport');
    }
  }

  @Watch('selection')
  public async onSelectionChange() {
    if (this.canFetch) {
      const { data } = await api.getTips(
        this.selection.venue as string,
        moment(this.selection.from).format('YYYY-MM-DD'),
        moment(this.selection.to).format('YYYY-MM-DD'),
      );
      this.tipsData = data;
    }
  }
}
